import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import * as TagGetMany from '../../server/routes/spaces/tags/get-many';
import * as TagGet from '../../server/routes/tags/get';
import * as TagPost from '../../server/routes/tags/post';
import { generatePath } from '../../server/services/utils.service';
import type { OrgTag } from '../../server/types/org-types';
import type { DeepReadonly } from '../../server/util-type';
import { AuthUserService } from './auth-user.service';
import { WebSocketService } from './web-socket.service';

@Injectable({
  providedIn: 'root',
})
export class TagService {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private authUserService = inject(AuthUserService);
  private webSocketService = inject(WebSocketService);

  private tagStores: {
    orgId: string;
    spaceId: string;
    tags: OrgTag[];
    allTagsSubject: ReplaySubject<OrgTag[]>;
    allTagsObservable: Observable<OrgTag[]>;
  }[] = [];

  private tagSubject = new Subject<OrgTag>();
  tagObservable = this.tagSubject.asObservable();

  constructor() {
    this.startWebSocket();

    this.authUserService.allUserOrgObjs.subscribe((userOrgs) => {
      userOrgs.forEach(({ org, user }) => {
        org.spaces.forEach((space) => {
          // Only get spaces this user is part of
          if (space.users.map((u) => u._id).includes(user._id))
            this.getAllTagsFromServer({ orgId: org._id, spaceId: space._id });
        });
      });
    });
  }

  private startWebSocket(): void {
    this.webSocketService.pushObservable.subscribe((socketResult) => {
      if (socketResult.service === 'tag') {
        const { tags } = socketResult.data;
        tags.forEach((tag) => this.updateTag(tag));
        _.uniq(tags.map((t) => t.spaceId)).forEach((spaceId) => {
          const first = tags[0];
          if (first)
            this.sendAll({ orgId: first.orgId, spaceId: first.spaceId });
        });
      }
    });
  }

  private getTagStore({ orgId, spaceId }: { orgId: string; spaceId: string }) {
    let tagStore = this.tagStores.find(
      (o) => o.orgId === orgId && o.spaceId === spaceId
    );
    if (!tagStore) {
      const allTagsSubject = new ReplaySubject<OrgTag[]>();
      const allTagsObservable = allTagsSubject.asObservable();
      tagStore = {
        orgId,
        spaceId,
        tags: [],
        allTagsSubject,
        allTagsObservable,
      };
      this.tagStores.push(tagStore);
    }
    return tagStore;
  }

  allTags({ orgId, spaceId }: { orgId: string; spaceId: string }) {
    return this.getTagStore({ orgId, spaceId }).allTagsObservable;
  }

  getAllTagsSync({
    orgId,
    spaceId,
  }: {
    orgId: string;
    spaceId: string;
  }): DeepReadonly<OrgTag>[] {
    const tagStore = this.getTagStore({ orgId, spaceId });

    return tagStore.tags;
  }

  createTag({
    orgId,
    spaceId,
    name: inputName,
  }: {
    orgId: string;
    spaceId: string;
    name: string;
  }) {
    const tagStore = this.getTagStore({ orgId, spaceId });
    const name = inputName
      .trim()
      .replace('  ', ' ')
      .replace('  ', ' ')
      .replace('  ', ' '); // Lazily turn multiple spaces into single spaces

    const result = new ReplaySubject<OrgTag>(1);

    const existingTag = tagStore.tags.find(
      (t) => t.name.toLocaleLowerCase() === name.toLocaleLowerCase()
    );

    if (existingTag) {
      result.next(existingTag);
      result.complete();
      return result.asObservable();
    }

    const body: TagPost.ReqBody = { name, spaceId };
    const path = generatePath<TagPost.ReqParams>('/api/orgs/:orgId/tags', {
      orgId,
    });

    this.http.post<TagPost.ResBody>(path, body).subscribe({
      next: (tag) => {
        const tagFromStore = this.updateTag(tag);
        this.sendAll({ orgId, spaceId });
        result.next(tagFromStore);
        result.complete();
      },
      error: (err) => {
        this.snackBar.open('ERROR: could not create tag.', 'Dismiss', {
          duration: 5000,
        });
        result.error(err);
        result.complete();
      },
    });

    return result.asObservable();
  }

  private updateTag(tag: OrgTag) {
    const { _id, orgId, spaceId } = tag;

    const tagStore = this.getTagStore({ orgId, spaceId });
    const index = tagStore.tags.findIndex((t) => t._id === _id);
    const existing = tagStore.tags[index];
    // If existing is newer don't update
    if (existing && existing.updatedAt > tag.updatedAt) return existing;

    const tagCopy = _.cloneDeep(tag);
    if (index >= 0) tagStore.tags[index] = tagCopy;
    else tagStore.tags.push(tagCopy);

    this.tagSubject.next(tagCopy);

    return tagCopy;
  }

  private sendAll({ orgId, spaceId }: { orgId: string; spaceId: string }) {
    const tagStore = this.getTagStore({ orgId, spaceId });
    tagStore.allTagsSubject.next(tagStore.tags);
  }

  getTag({
    orgId,
    spaceId,
    tagId,
  }: {
    orgId: string;
    spaceId: string;
    tagId: string;
  }) {
    const result = new ReplaySubject<OrgTag>(1);
    const tagStore = this.getTagStore({ orgId, spaceId });
    const existingTag = tagStore.tags.find((t) => t._id === tagId);

    if (existingTag) {
      result.next(existingTag);
      return result;
    }

    const path = generatePath<TagGet.ReqParams>(
      '/api/orgs/:orgId/tags/:tagId',
      { orgId, tagId }
    );

    this.http.get<TagGet.ResBody>(path).subscribe({
      next: (tag) => {
        const tagFromStore = this.updateTag(tag);
        this.sendAll({ orgId, spaceId });
        result.next(tagFromStore);
      },
      error: (err) => {
        result.error(err);
      },
    });

    return result.asObservable();
  }

  private getAllTagsFromServer({
    orgId,
    spaceId,
  }: {
    orgId: string;
    spaceId: string;
  }) {
    const path = generatePath<TagGetMany.ReqParams>(
      '/api/orgs/:orgId/spaces/:spaceId/tags',
      { orgId, spaceId }
    );

    this.http.get<TagGetMany.ResBody>(path).subscribe({
      next: (tags) => {
        tags.forEach((tag) => this.updateTag(tag));
        this.sendAll({ orgId, spaceId });
      },
      error: (err) => {
        console.error(err);
      },
    });
  }
}
